import React, { useCallback, useEffect } from 'react'
import { arrayOf, number, oneOf, shape, string } from 'prop-types'
import NursingCalculator from './partials/NursingCalculator'
import { getCarePriceData } from 'settings/xhr/axiosRequests'
import {
  DAY_AND_NIGHT_CARE,
  LONG_TERM_CARE,
  SHORT_TERM_CARE,
} from './constants'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import qs from 'query-string'

const NursingCalculatorContainer = ({
  data,
  type,
  detailId,
  defaultVisitingDays,
  preSelectedFocus,
  preSelectedFullStationaryTimeSpan,
}) => {
  const storeVisitingDays = useSelector(
    (state) => state.search.params.PR_PFDAYS
  )
  const location = useLocation()

  // local state
  /**
   * currently selected care type
   */
  const [selectedCareFocus, setSelectedCareFocus] = React.useState(
    data.careFocus.findIndex(
      (careFocus) => careFocus.focusId === preSelectedFocus
    ) >= 0
      ? preSelectedFocus
      : data.careFocus[0]?.focusId
  )

  // local state
  /**
   * currently selected duration
   */
  const [selectedFullStationaryTimeSpan, setSelectedFullStationaryTimeSpan] =
    React.useState(
      !!data.fullStationaryTimeSpan
        ? data.fullStationaryTimeSpan?.findIndex(
            (timeSpan) => timeSpan.id === preSelectedFullStationaryTimeSpan
          ) >= 0
          ? preSelectedFullStationaryTimeSpan
          : data.fullStationaryTimeSpan[0]?.id
        : ''
    )

  /**
   * current set of prices and calculated values for selected care focus
   */
  const [careData, setCareData] = React.useState(data)

  /**
   * current amount of visiting days used for calculation and display
   */
  const [activeVisitingDays, setActiveVisitingDays] = React.useState(
    storeVisitingDays
      ? storeVisitingDays
      : defaultVisitingDays > 0
      ? defaultVisitingDays
      : ''
  )

  /**
   * current value of visiting days from visiting-days-input
   */
  const [selectedVisitingDays, setSelectedVisitingDays] = React.useState(
    storeVisitingDays
      ? storeVisitingDays
      : defaultVisitingDays > 0
      ? defaultVisitingDays
      : ''
  )

  /**
   * fetches new carehome calculation data
   * @returns {object}
   */
  const fetchCareData = useCallback(
    (value = '') => {
      let endpoint = ''
      let queryParams = ''
      if (type === SHORT_TERM_CARE || type === LONG_TERM_CARE) {
        endpoint = 'carehome'
        if (type === SHORT_TERM_CARE) {
          queryParams =
            value > 0
              ? `calculationType=shortTerm&daysShortTimeCare=${ value }`
              : 'calculationType=shortTerm'
        }
        if (type === LONG_TERM_CARE) {
          queryParams = `calculationType=longTerm&fullStationaryTimeSpan=${ value }`
        }
      }
      if (type === DAY_AND_NIGHT_CARE) {
        endpoint = 'dayandnightcare'
        queryParams = value > 0 ? `PR_PFDAYS=${ value }` : ''
      }

      // XHR API
      getCarePriceData({
        endpoint,
        id: detailId,
        queryParams,
      })
        .then((calcData) => {
          if (!calcData.message && calcData.result[type].careFocus.length > 0) {
            setCareData({ ...calcData.result[type] })
          }
        })
        .catch((error) => console.log(error))
    },
    [type, detailId]
  )

  // set visiting days from url param
  React.useEffect(() => {
    const parsed = qs.parse(location.search)
    if (parsed?.PR_PFDAYS) {
      setActiveVisitingDays(parsed.PR_PFDAYS)
      setSelectedVisitingDays(parsed.PR_PFDAYS)
    }
  }, [location])

  useEffect(() => {
    if (type === SHORT_TERM_CARE || type === DAY_AND_NIGHT_CARE) {
      fetchCareData(activeVisitingDays)
    }
    if (type === LONG_TERM_CARE) {
      fetchCareData(selectedFullStationaryTimeSpan)
    }
  }, [activeVisitingDays, selectedFullStationaryTimeSpan, type, fetchCareData])

  /**
   * updates state of careType with value from care-type-select
   * @param {string} value
   * @returns {void}
   */
  const handleCareFocusSelect = (value) => {
    setSelectedCareFocus(
      careData.careFocus.find((option) => option.focusId === value)?.focusId
    )
  }

  /**
   * update
   * @param {string} value
   * @returns {void}
   */
  const handleFullStationaryTimeSpanSelect = (value) => {
    setSelectedFullStationaryTimeSpan(
      careData.fullStationaryTimeSpan.find((option) => option.id === value)?.id
    )
  }

  /**
   * updates state of selectedVisitingDays with event-target value from visiting-days-input
   * @param {false|number} value
   * @returns {void}
   */
  const handleVisitingDaysInput = (value) => {
    setSelectedVisitingDays(value > 30 ? 30 : value)
  }

  /**
   * applies value of selectedVisitingDays to activeVisitingDays to update the view and calculation
   * @returns {void}
   */
  const handleCalculateButtonClick = () => {
    if (selectedVisitingDays < 1) {
      setSelectedVisitingDays('')
    }
    setActiveVisitingDays(selectedVisitingDays)
  }

  return careData && careData.careFocus.length > 0 ? (
    <NursingCalculator
      type={ type }
      careData={ careData }
      selectedCareFocus={ selectedCareFocus }
      selectedVisitingDays={ selectedVisitingDays }
      selectedFullStationaryTimeSpan={ selectedFullStationaryTimeSpan }
      activeVisitingDays={ activeVisitingDays }
      handleCareFocusSelect={ handleCareFocusSelect }
      handleFullStationaryTimeSpanSelect={ handleFullStationaryTimeSpanSelect }
      handleVisitingDaysInput={ handleVisitingDaysInput }
      handleCalculateButtonClick={ handleCalculateButtonClick }
    />
  ) : null
}

NursingCalculatorContainer.propTypes = {
  data: shape({
    careFocus: arrayOf(
      shape({
        abCode: string.isRequired,
        focus: string.isRequired,
        focusId: string.isRequired,
        careLevels: arrayOf(
          shape({
            careLevel: number,
            ownShare: number,
            invest: number,
            priceDays: number,
            priceMonth: number,
            housingAndCare: number,
          })
        ),
      })
    ).isRequired,
    fullStationaryTimeSpan: arrayOf(
      shape({
        id: string.isRequired,
        label: string.isRequired,
      })
    ),
    additionalInformation: string.isRequired,
    costInformation: string,
  }).isRequired,
  type: oneOf([LONG_TERM_CARE, SHORT_TERM_CARE, DAY_AND_NIGHT_CARE]).isRequired,
  detailId: string.isRequired,
  defaultVisitingDays: number,
  preSelectedFocus: string,
}

export default NursingCalculatorContainer
